/**
 * メディアエクスポート統合ファサードサービス (MediaExportService)
 * aidlc-docs/construction/media_export/docs/component_architecture.md 準拠
 * 公開ポート IMediaExportPort を完全実装
 */

import type {
  ExportProgressDTO,
  ExportRequestDTO,
  ExportResultDTO,
  IMediaExportPort,
} from '../ports/types';
import { FrameCapturer } from './FrameCapturer';
import { WatermarkCompositor } from './WatermarkCompositor';
import { EncoderAdapter } from './EncoderAdapter';

export interface MediaExportServiceDependencies {
  capturer?: FrameCapturer;
  compositor?: WatermarkCompositor;
  encoder?: EncoderAdapter;
  createCanvasFn?: (width: number, height: number) => HTMLCanvasElement;
}

export class MediaExportService implements IMediaExportPort {
  private readonly capturer: FrameCapturer;
  private readonly compositor: WatermarkCompositor;
  private readonly encoder: EncoderAdapter;
  private isProcessing: boolean = false;
  private createCanvasFn?: (width: number, height: number) => HTMLCanvasElement;

  constructor(dependencies: MediaExportServiceDependencies = {}) {
    this.createCanvasFn = dependencies.createCanvasFn;
    this.capturer = dependencies.capturer || new FrameCapturer();
    this.compositor = dependencies.compositor || new WatermarkCompositor();
    this.encoder =
      dependencies.encoder ||
      new EncoderAdapter({
        createCanvasFn: dependencies.createCanvasFn,
      });
  }

  /**
   * アニメーション録画およびエンコードを実行
   */
  public async exportAnimation(
    dto: ExportRequestDTO,
    onProgress?: (progress: ExportProgressDTO) => void
  ): Promise<ExportResultDTO> {
    if (this.isProcessing) {
      throw new Error('An export operation is already in progress.');
    }

    if (!dto.sourceCanvas) {
      throw new Error('sourceCanvas is required for exportAnimation.');
    }

    this.isProcessing = true;

    try {
      // 1. キャプチャ段階 (stage: 'capturing')
      const captureResult = await this.capturer.startCapture({
        sourceCanvas: dto.sourceCanvas,
        durationMs: dto.durationMs,
        fps: dto.fps,
        onProgress,
        createCanvasFn: this.createCanvasFn,
      });

      // 2. 透かし合成段階 (Web無料版: バッジ合成 / アプリ有料版: 完全パススルー)
      const compositedFrames = this.compositor.compositeFrames(captureResult.frames, {
        applyWatermark: dto.applyWatermark,
        watermarkText: dto.watermarkText,
        createCanvasFn: this.createCanvasFn,
      });

      // 3. エンコード段階 (stage: 'encoding')
      const exportResult = await this.encoder.encode(dto, compositedFrames, onProgress);

      // キャプチャバッファのクリーンアップ
      captureResult.dispose();

      return exportResult;
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * 実行中のエクスポート処理を中止
   */
  public cancelExport(): void {
    this.capturer.cancel();
    this.encoder.cancel();
    this.isProcessing = false;
  }

  /**
   * 処理中フラグの確認
   */
  public getIsProcessing(): boolean {
    return this.isProcessing;
  }
}
